/**
 * AddToProductBottomBar — 1:1 visuelles Pendant zu ProductImagesBottomBar:
 * Sticky bottom, Slide-up bei hasChanges, AlertTriangle + Counter + Discard/Save.
 * Eigene Komponente (statt direkter Reuse) damit der Overlay-Kontext keine
 * Abhängigkeit auf den Produkt-Detail-Subtab zieht.
 */
import { AlertTriangle, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import { cn } from '@/lib/utils'

interface AddToProductBottomBarProps {
  hasChanges: boolean
  saving: boolean
  channelCount: number
  imageCount: number
  onSave: () => void
  onDiscard: () => void
}

export function AddToProductBottomBar({
  hasChanges,
  saving,
  channelCount,
  imageCount,
  onSave,
  onDiscard,
}: AddToProductBottomBarProps) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[70]',
        'transform transition-transform duration-300 ease-out',
        hasChanges ? 'translate-y-0' : 'translate-y-full',
      )}
      data-testid="atp-bottom-bar"
      aria-hidden={!hasChanges}
    >
      <div className="bg-neutral-900 dark:bg-neutral-800 border-t border-neutral-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/20">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('common.unsavedChanges')}</p>
                <p className="text-neutral-400 text-sm">
                  {channelCount > 0 && (
                    <>
                      {t(
                        channelCount === 1
                          ? 'assets.channelAffected'
                          : 'assets.channelsAffected',
                        { count: channelCount },
                      )}
                    </>
                  )}
                  {channelCount > 0 && imageCount > 0 && ' • '}
                  {imageCount > 0 && (
                    <>
                      {t(
                        imageCount === 1
                          ? 'assets.imageChanged'
                          : 'assets.imagesChanged',
                        { count: imageCount },
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={onDiscard}
                disabled={saving}
                className="text-neutral-300 hover:text-white hover:bg-neutral-700"
              >
                {t('common.discard')}
              </Button>
              <Button
                variant="primary"
                onClick={onSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {t('common.save')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
